import { useState, useEffect } from "react";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Plus, ZoomIn, X, Languages, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const imgSrc = (im) => (im?.storage_path ? fileUrl(im.storage_path) : im?.url);

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium text-foreground">{value}</p>
    </div>
  );
}

export default function ProductDetailDialog({ article, open, onOpenChange, locked, onAdd }) {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(0);
  const [lbIndex, setLbIndex] = useState(null);

  const variants = Array.isArray(article?.variants) ? article.variants : [];
  const hasSizes = variants.some((v) => v.size);
  const vkey = (v) => `${v.product_id}::${v.size || "unica"}`;
  const [selKey, setSelKey] = useState("");

  // dynamic translation
  const desc = (article?.description || "").trim();
  const specs = (article?.tech_specs || "").trim();
  const [trDesc, setTrDesc] = useState(null);
  const [trSpecs, setTrSpecs] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (open) { setActive(0); setSelKey(hasSizes ? "" : (variants[0] ? vkey(variants[0]) : "")); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article?.group_key]);

  useEffect(() => {
    if (!open) return;
    if (lang === "it" || (!desc && !specs)) { setTrDesc(null); setTrSpecs(null); setTranslating(false); return; }
    let cancelled = false;
    const payload = [];
    if (desc) payload.push(desc);
    if (specs && specs !== desc) payload.push(specs);
    setTranslating(true);
    api.post("/portal/translate", { texts: payload, target_lang: lang })
      .then(({ data }) => {
        if (cancelled) return;
        const tr = data.translations || [];
        let idx = 0;
        if (desc) { setTrDesc(tr[idx] ?? desc); idx += 1; }
        if (specs && specs !== desc) { setTrSpecs(tr[idx] ?? specs); }
      })
      .catch(() => { if (!cancelled) { setTrDesc(null); setTrSpecs(null); } })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang, article?.group_key]);

  if (!article) return null;

  const images = Array.isArray(article.images) ? article.images : [];
  const pr = article.pricing || {};
  const colors = Array.isArray(article.colors) ? article.colors : [];
  const mainImg = images[active] || images[0];
  const showPrices = !locked && pr && pr.price_excl_vat != null;
  const selected = hasSizes ? variants.find((v) => vkey(v) === selKey) : variants[0];
  const stock = selected ? selected.stock : (article.total_stock ?? 0);
  const translated = lang !== "it" && !translating;
  const descShown = translated && trDesc ? trDesc : desc;
  const specsShown = translated && trSpecs ? trSpecs : specs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-4xl overflow-y-auto border-border bg-brema-card"
        data-testid="product-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-foreground">{t("product.detail_title")}</DialogTitle>
          <DialogDescription className="sr-only">{article.name}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Gallery */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => mainImg && setLbIndex(active)}
              data-testid="product-detail-zoom"
              className="group relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-xl border border-border bg-brema-bg"
            >
              {mainImg ? (
                <img src={imgSrc(mainImg)} alt={article.name} className="h-full w-full object-cover" data-testid="product-detail-main-image" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>
              )}
              {article.category && (
                <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">{article.category}</span>
              )}
              {mainImg && (
                <span className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] text-white opacity-80 backdrop-blur transition-opacity group-hover:opacity-100">
                  <ZoomIn className="h-3.5 w-3.5" /> {t("product.zoom")}
                </span>
              )}
            </button>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2" data-testid="product-detail-thumbs">
                {images.map((im, i) => (
                  <button
                    key={imgSrc(im) || i}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`product-detail-thumb-${i}`}
                    className={`aspect-square overflow-hidden rounded-lg border bg-brema-bg transition ${i === active ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/50"}`}
                  >
                    <img src={imgSrc(im)} alt={`${article.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info (read-only) */}
          <div className="space-y-5">
            <div>
              <h3 className="text-xl font-bold text-foreground" data-testid="product-detail-name">{article.name}</h3>
              {(article.line || article.model_code) && (
                <p className="text-sm text-muted-foreground">
                  {article.line && <span className="font-bold uppercase tracking-wide text-foreground">{article.line}</span>}
                  {article.line && article.model_code ? " · " : ""}{article.model_code}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {article.category && <Meta label={t("product.category")} value={article.category} />}
              {article.line && <Meta label={t("product.line_label")} value={article.line} />}
              {article.model_code && <Meta label={t("product.code")} value={article.model_code} />}
              {article.gender && <Meta label={t("product.gender")} value={article.gender} />}
              {colors.length > 0 && <Meta label={t("product.colors")} value={colors.join(", ")} />}
              {variants.length > 0 && (
                <Meta label={t("product.sizes")} value={variants.map((v) => v.size || t("product.size_unica")).join(", ")} />
              )}
            </div>

            {showPrices && (
              <div className="space-y-1 rounded-lg bg-brema-bg p-3 text-sm" data-testid="product-detail-prices">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>{t("product.list_public")}</span>
                  <span className="text-foreground">{eur(pr.price_incl_vat)} <span className="text-[10px] text-muted-foreground">{t("product.vat_incl")}</span></span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>{t("product.list_excl")}</span><span>{eur(pr.price_excl_vat)}</span>
                </div>
              </div>
            )}

            {/* Add to order (read/consult without leaving) */}
            {!locked && variants.length > 0 && onAdd && (
              <div className="space-y-2 rounded-lg border border-border bg-brema-bg/60 p-3" data-testid="product-detail-order-box">
                {hasSizes && (
                  <Select value={selKey} onValueChange={setSelKey}>
                    <SelectTrigger className="h-9" data-testid="product-detail-size-select">
                      <SelectValue placeholder={t("product.size_ph")} />
                    </SelectTrigger>
                    <SelectContent>
                      {variants.map((v) => (
                        <SelectItem key={vkey(v)} value={vkey(v)} disabled={v.stock <= 0}>
                          {v.size || t("product.size_unica")}{v.stock <= 0 ? ` — ${t("product.sold_out")}` : ` (${v.stock})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selected && (
                  <p className={`text-xs font-medium ${stock <= 10 ? "text-brema-red" : "text-positive"}`}>
                    {stock <= 10 ? t("product.only_pcs", { n: stock }) : t("product.pcs_avail", { n: stock })}
                  </p>
                )}
                <Button
                  data-testid="product-detail-add-btn"
                  disabled={hasSizes && !selected}
                  onClick={() => onAdd(article, selected)}
                  className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  <Plus className="mr-1 h-4 w-4" /> {t("product.add_to_order")}
                </Button>
              </div>
            )}

            {/* Description + tech specs (auto-translated to reseller language) */}
            {lang !== "it" && (desc || specs) && (
              <div className="flex items-center gap-1.5 text-[11px] text-brand" data-testid="product-detail-translated-badge">
                {translating
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> {t("product.translating")}</>
                  : <><Languages className="h-3 w-3" /> {t("product.auto_translated")}</>}
              </div>
            )}
            {descShown && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{t("product.description_label")}</h4>
                <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="product-detail-description">{descShown}</p>
              </div>
            )}
            {specsShown && specsShown !== descShown && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{t("product.tech_label")}</h4>
                <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="product-detail-specs">{specsShown}</p>
              </div>
            )}
            {!desc && !specs && (
              <p className="text-sm text-muted-foreground">{t("product.no_description")}</p>
            )}
          </div>
        </div>

        {/* Full-screen lightbox */}
        {lbIndex !== null && images[lbIndex] && (
          <div
            data-testid="product-detail-lightbox"
            onClick={() => setLbIndex(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          >
            <img src={imgSrc(images[lbIndex])} alt={article.name} className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLbIndex(null); }}
              data-testid="product-detail-lightbox-close"
              className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white transition hover:bg-white/30"
            >
              <X className="h-5 w-5" />
            </button>
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLbIndex((lbIndex - 1 + images.length) % images.length); }}
                  data-testid="product-detail-lightbox-prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white transition hover:bg-white/30"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLbIndex((lbIndex + 1) % images.length); }}
                  data-testid="product-detail-lightbox-next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white transition hover:bg-white/30"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white" data-testid="product-detail-lightbox-counter">{lbIndex + 1} / {images.length}</span>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
